// Content Create Page
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
} from '@mui/icons-material';

import { useCreateContent } from '../hooks/useContent';
import { ContentForm } from '../types';
import MediaFileSelector from '../../../components/MediaFileSelector';
import { useAppSelector } from '../../../store/hooks';
import { selectSelectedMuseum } from '../../../store/slices/museumSlice';
import { waypointService } from '../../waypoint/services/waypointService';
const ContentCreatePage: React.FC = () => {
  const navigate = useNavigate();

  // Get current user and selected museum from Redux store
  const { user } = useAppSelector(state => state.auth);
  const selectedMuseum = useAppSelector(selectSelectedMuseum);
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin; // Only Admin can edit, Super User has same permissions as User

  // Redirect non-admin users
  useEffect(() => {
    if (!canEdit) {
      navigate('/content');
    }
  }, [canEdit, navigate]);

  // State
  const [selectedWaypoint, setSelectedWaypoint] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);

  // Media file selector state
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState(false);
  const [currentMediaType, setCurrentMediaType] = useState<'image' | 'video' | 'audio'>('image');
  const [currentMediaField, setCurrentMediaField] = useState<string>('');
  const [currentMediaScreen, setCurrentMediaScreen] = useState<'primary' | 'secondary'>('primary');

  // Form state
  const [contentForm, setContentForm] = useState<ContentForm>({
    waypoint_name: '',
    primary_duration: 60,
    primary_long_text_content: '',
    primary_short_text_content: '',
    primary_image_url: '',
    primary_video_url: '',
    primary_audio_url: '',
    primary_audio_volume: '7',
    primary_genai_context: '',
    primary_optional_text: '',
    primary_display_template: 'TEXT',
    secondary_long_text_content: '',
    secondary_image_url: '',
    secondary_video_url: '',
    secondary_audio_url: '',
    secondary_optional_text: '',
    secondary_display_template: 'TEXT',
  });

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Hooks
  const createContent = useCreateContent();

  const [availableWaypointNames, setAvailableWaypointNames] = useState<string[]>([]);
  const [loadingWaypoints, setLoadingWaypoints] = useState(false);

  useEffect(() => {
    const museumCode = selectedMuseum?.museum_code;
    if (!museumCode) {
      setAvailableWaypointNames([]);
      return;
    }

    const loadWaypoints = async () => {
      setLoadingWaypoints(true);
      try {
        const versions = await waypointService.listVersions(museumCode, '1');
        const latest = versions?.[0];
        const fileData = Array.isArray(latest?.file_data) ? latest.file_data : [];
        const landmarkWaypoints = fileData.filter(item => item?.type === 'landmark');
        const names = landmarkWaypoints.map(
          (item, index) => item?.title || item?.nav || item?.name || `Waypoint ${index + 1}`,
        );
        setAvailableWaypointNames(names);
      } catch (error) {
        console.error('Failed to load waypoint versions:', error);
        setAvailableWaypointNames([]);
      } finally {
        setLoadingWaypoints(false);
      }
    };

    loadWaypoints();
  }, [selectedMuseum?.museum_code]);

  // Update form when waypoint changes
  useEffect(() => {
    if (selectedWaypoint) {
      setContentForm(prev => ({
        ...prev,
        waypoint_name: selectedWaypoint,
      }));
    }
  }, [selectedWaypoint]);

  const handleFormFieldChange = (field: keyof ContentForm, value: any) => {
    setContentForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleOpenMediaSelector = (mediaType: 'image' | 'video' | 'audio', fieldName: string) => {
    setCurrentMediaType(mediaType);
    setCurrentMediaField(fieldName);
    setCurrentMediaScreen(fieldName.startsWith('secondary') ? 'secondary' : 'primary');
    setMediaSelectorOpen(true);
  };

  const handleMediaSelect = (fileUrl: string) => {
    setContentForm(prev => ({
      ...prev,
      [currentMediaField]: fileUrl,
    }));
    if (fileUrl) {
      setMediaSelectorOpen(false);
    }
  };

  const renderImagePreview = (url: string) => {
    if (!url) return null;
    return (
      <Box
        sx={{
          mt: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          maxWidth: 360,
        }}
      >
        <Box
          component="img"
          src={url}
          alt="Selected preview"
          sx={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Box>
    );
  };

  const renderVideoPreview = (url: string) => {
    if (!url) return null;
    return (
      <Box
        sx={{
          mt: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          maxWidth: 480,
        }}
      >
        <Box
          component="video"
          src={url}
          controls
          sx={{ width: '100%', height: 'auto', display: 'block', backgroundColor: 'black' }}
        />
      </Box>
    );
  };

  const validateForm = (): string | null => {
    // Check waypoint selection
    if (!selectedWaypoint) {
      return 'Please select a waypoint';
    }

    // Check museum selection
    if (!selectedMuseum || !selectedMuseum.id) {
      return 'Please select a museum from the navbar';
    }

    // Check primary screen required fields
    if (!contentForm.primary_long_text_content.trim()) {
      return 'Primary Long Text is required';
    }
    if (!contentForm.primary_short_text_content.trim()) {
      return 'Primary Short Text is required';
    }
    if (
      !contentForm.primary_duration ||
      (typeof contentForm.primary_duration === 'string' && contentForm.primary_duration === '') ||
      (typeof contentForm.primary_duration === 'number' && contentForm.primary_duration <= 0)
    ) {
      return 'Primary Duration must be greater than 0';
    }

    // Check secondary screen required fields
    if (!contentForm.secondary_long_text_content.trim()) {
      return 'Secondary Long Text is required';
    }

    return null; // No validation errors
  };

  const handleSave = async () => {
    try {
      // Validate form
      const validationError = validateForm();
      if (validationError) {
        setSnackbarMessage(validationError);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      // Prepare form data with required fields
      // selectedMuseum is validated in validateForm() above, so it's safe to use here
      const formData = {
        ...contentForm,
        waypoint_name: selectedWaypoint,
        museum_id: selectedMuseum!.id, // Non-null assertion: validated in validateForm()
      };

      console.log('API Request Data:', formData);

      await createContent.mutate(formData);
      setSnackbarMessage('Content created successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate('/content');
    } catch (error: any) {
      console.error('API Error Response:', error);
      setSnackbarMessage(error.message || 'Failed to create content');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Create Content</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button startIcon={<CancelIcon />} onClick={() => navigate('/content')}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={createContent.loading || !selectedWaypoint}
          >
            {createContent.loading ? 'Creating...' : 'Create Content'}
          </Button>
        </Box>
      </Box>

      {/* Waypoint Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Waypoint</InputLabel>
            <Select
              value={selectedWaypoint}
              onChange={e => setSelectedWaypoint(e.target.value)}
              label="Select Waypoint"
              disabled={loadingWaypoints || availableWaypointNames.length === 0}
            >
              {availableWaypointNames.map((waypointName, index) => (
                <MenuItem key={`${waypointName}-${index}`} value={waypointName}>
                  {waypointName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <Card>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Primary" />
          <Tab label="Secondary" />
        </Tabs>

        {/* Tab Content */}
        <CardContent>
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Primary Screen Content
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Duration (seconds) *"
                  value={contentForm.primary_duration}
                  onChange={e => {
                    const value = e.target.value;
                    if (value === '' || value === '0') {
                      handleFormFieldChange('primary_duration', '');
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue)) {
                        handleFormFieldChange('primary_duration', numValue);
                      }
                    }
                  }}
                  sx={{ minWidth: 120 }}
                  required
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={contentForm.primary_display_template}
                    onChange={e => handleFormFieldChange('primary_display_template', e.target.value)}
                    label="Template"
                  >
                    <MenuItem value="TEXT">Text</MenuItem>
                    <MenuItem value="IMAGE">Image</MenuItem>
                    <MenuItem value="VIDEO">Video</MenuItem>
                    <MenuItem value="AUDIO">Audio</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                label="Short Text *"
                multiline
                rows={2}
                value={contentForm.primary_short_text_content}
                onChange={e => handleFormFieldChange('primary_short_text_content', e.target.value)}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Long Text *"
                multiline
                rows={4}
                value={contentForm.primary_long_text_content}
                onChange={e => handleFormFieldChange('primary_long_text_content', e.target.value)}
                sx={{ mb: 2 }}
                required
              />

              {/* Media Files */}
              <Typography variant="subtitle1" gutterBottom>
                Media Files:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ImageIcon />
                  <TextField
                    label="Image URL"
                    fullWidth
                    value={contentForm.primary_image_url}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Use the Select button to choose an image file"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => handleOpenMediaSelector('image', 'primary_image_url')}
                  >
                    {contentForm.primary_image_url ? 'Change' : 'Select'}
                  </Button>
                </Box>
                {renderImagePreview(contentForm.primary_image_url)}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VideoIcon />
                  <TextField
                    label="Video URL"
                    fullWidth
                    value={contentForm.primary_video_url}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Use the Select button to choose a video file"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => handleOpenMediaSelector('video', 'primary_video_url')}
                  >
                    {contentForm.primary_video_url ? 'Change' : 'Select'}
                  </Button>
                </Box>
                {renderVideoPreview(contentForm.primary_video_url)}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AudioIcon />
                  <TextField
                    label="Audio URL"
                    fullWidth
                    value={contentForm.primary_audio_url}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Use the Select button to choose an audio file"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => handleOpenMediaSelector('audio', 'primary_audio_url')}
                  >
                    {contentForm.primary_audio_url ? 'Change' : 'Select'}
                  </Button>
                </Box>
              </Box>

              {/* Audio Volume */}
              <TextField
                label="Audio Volume (0–10)"
                value={contentForm.primary_audio_volume}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '' || /^([0-9]|10)$/.test(val)) {
                    handleFormFieldChange('primary_audio_volume', val);
                  }
                }}
                sx={{ mb: 2, minWidth: 140 }}
                helperText="Playback volume level"
              />

              {/* GenAI Context */}
              <TextField
                fullWidth
                label="GenAI Context"
                multiline
                rows={2}
                value={contentForm.primary_genai_context}
                onChange={e => handleFormFieldChange('primary_genai_context', e.target.value)}
                sx={{ mb: 2 }}
                helperText="Optional context string for GenAI features (leave blank to disable)"
              />

              {/* Optional Text */}
              <TextField
                fullWidth
                label="Optional Text"
                multiline
                rows={2}
                value={contentForm.primary_optional_text}
                onChange={e => handleFormFieldChange('primary_optional_text', e.target.value)}
                sx={{ mb: 2 }}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Secondary Screen Content
              </Typography>

              <FormControl sx={{ mb: 2 }}>
                <InputLabel>Template</InputLabel>
                <Select
                  value={contentForm.secondary_display_template}
                  onChange={e => handleFormFieldChange('secondary_display_template', e.target.value)}
                  label="Template"
                >
                  <MenuItem value="TEXT">Text</MenuItem>
                  <MenuItem value="IMAGE">Image</MenuItem>
                  <MenuItem value="VIDEO">Video</MenuItem>
                  <MenuItem value="AUDIO">Audio</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Long Text *"
                multiline
                rows={4}
                value={contentForm.secondary_long_text_content}
                onChange={e => handleFormFieldChange('secondary_long_text_content', e.target.value)}
                sx={{ mb: 2 }}
                required
              />

              {/* Media Files */}
              <Typography variant="subtitle1" gutterBottom>
                Media Files:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ImageIcon />
                  <TextField
                    label="Image URL"
                    fullWidth
                    value={contentForm.secondary_image_url}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Use the Select button to choose an image file"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => handleOpenMediaSelector('image', 'secondary_image_url')}
                  >
                    {contentForm.secondary_image_url ? 'Change' : 'Select'}
                  </Button>
                </Box>
                {renderImagePreview(contentForm.secondary_image_url)}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VideoIcon />
                  <TextField
                    label="Video URL"
                    fullWidth
                    value={contentForm.secondary_video_url}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Use the Select button to choose a video file"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => handleOpenMediaSelector('video', 'secondary_video_url')}
                  >
                    {contentForm.secondary_video_url ? 'Change' : 'Select'}
                  </Button>
                </Box>
                {renderVideoPreview(contentForm.secondary_video_url)}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AudioIcon />
                  <TextField
                    label="Audio URL"
                    fullWidth
                    value={contentForm.secondary_audio_url}
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Use the Select button to choose an audio file"
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    onClick={() => handleOpenMediaSelector('audio', 'secondary_audio_url')}
                  >
                    {contentForm.secondary_audio_url ? 'Change' : 'Select'}
                  </Button>
                </Box>
              </Box>

              {/* Optional Text */}
              <TextField
                fullWidth
                label="Optional Text"
                multiline
                rows={2}
                value={contentForm.secondary_optional_text}
                onChange={e => handleFormFieldChange('secondary_optional_text', e.target.value)}
                sx={{ mb: 2 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Media File Selector */}
      <MediaFileSelector
        open={mediaSelectorOpen}
        onClose={() => setMediaSelectorOpen(false)}
        onSelect={handleMediaSelect}
        fileType={currentMediaType}
        currentValue={contentForm[currentMediaField as keyof ContentForm] as string}
        screen={currentMediaScreen}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentCreatePage;
